/**
 * World save / load — and the foundation for lockstep multiplayer.
 *
 * A snapshot is a PLAIN JSON object: it captures everything the deterministic
 * sim needs to resume to the exact same state. Terrain cells are regenerated
 * from the compact seed + authored recipe rather than stored individually, which
 * keeps snapshots small and gives multiplayer clients the same reconstruction.
 *
 * The whole sim is already command-driven and deterministic (see types.ts /
 * commands.ts), so:
 *   - save / load  = take a snapshot, restore it later.
 *   - MP late-join = host sends a snapshot; client restores and follows the
 *                    shared command stream from that tick onward.
 *   - MP resync    = same snapshot mechanism whenever a desync is detected.
 *
 * Restoring is a perfect continuation point: restore(snapshot(w)) then stepping
 * yields the identical trajectory as stepping w (covered by serialize.test.ts).
 */
import { World } from "./world";
import type { Registry } from "../data/registry";
import { TERRAIN_TYPES, type TerrainType } from "../data/schema";
import type { Eid, PlayerState } from "./types";
import type {
  Building, Builder, Combat, Fauna, Garrison, Gatherer, Healer, Health, Ident,
  LineOfSight, Move, Owner, Producer, Rally, Resource, Transform,
} from "./components";
import { matchHexes, setTerrainRecipe, terrainRecipeFor } from "./match";
import type { TerrainRecipeOptions } from "./mapgen";
import { propTileCost, buildingTileCost } from "./pathfinding";
import { propRadius, buildingRadius } from "./systems/movement";
import { attachWorldHelpers } from "./commands";

/** Bump when the snapshot shape changes incompatibly. */
export const SNAPSHOT_VERSION = 4;

type Entry<T> = [Eid, T];

/** Deep, independent copy via the save format itself (JSON). Guarantees a
 *  snapshot shares NO mutable references with the live world — so stepping the
 *  world never corrupts a snapshot — and asserts the data is pure JSON. */
function clone<T>(x: T): T {
  return JSON.parse(JSON.stringify(x)) as T;
}

export interface WorldSnapshot {
  version: number;
  // map regeneration inputs (terrain is deterministic from these)
  cols: number;
  rows: number;
  size: number;
  seed: number;
  /** Added compatibly in v4; absent means the original procedural generator. */
  terrain?: TerrainRecipeOptions;
  // sim counters / rng
  tick: number;
  nextEid: number;
  rng: number;
  // tile occupancy (serialized verbatim, NOT re-derived — re-deriving from entity
  // positions risks float-rounding a node onto a neighbouring hex and desyncing
  // pathfinding, which the determinism test would catch)
  blocked: number[];
  resourceHexKeys: number[];
  dropsites: [number, Eid[]][];
  // economy
  players: PlayerState[];
  // component stores (eid -> data)
  transform: Entry<Transform>[];
  owner: Entry<Owner>[];
  ident: Entry<Ident>[];
  health: Entry<Health>[];
  move: Entry<Move>[];
  combat: Entry<Combat>[];
  gatherer: Entry<Gatherer>[];
  builder: Entry<Builder>[];
  producer: Entry<Producer>[];
  resource: Entry<Resource>[];
  building: Entry<Building>[];
  rally: Entry<Rally>[];
  los: Entry<LineOfSight>[];
  healer: Entry<Healer>[];
  fauna: Entry<Fauna>[];
  garrison: Entry<Garrison>[];
  carcass: Eid[];
}

/** Assemble the snapshot object — shares references with the live world, so it
 *  must be cloned (snapshot) or serialized (snapshotJSON) immediately, before
 *  the world is stepped again. */
function build(world: World): WorldSnapshot {
  const map = <T>(m: Map<Eid, T>): Entry<T>[] => [...m.entries()];
  return {
    version: SNAPSHOT_VERSION,
    cols: world.cols,
    rows: world.rows,
    size: world.size,
    seed: world.seed,
    terrain: terrainRecipeFor(world),
    tick: world.tick,
    nextEid: world.getEidCounter(),
    rng: world.rng.getState(),
    blocked: Array.from(world.blocked),
    resourceHexKeys: [...world.resourceHexKeys],
    dropsites: [...world.dropsitesByPlayer].map(([p, set]) => [p, [...set]] as [number, Eid[]]),
    players: [...world.players.values()],
    transform: map(world.transform),
    owner: map(world.owner),
    ident: map(world.ident),
    health: map(world.health),
    move: map(world.move),
    combat: map(world.combat),
    gatherer: map(world.gatherer),
    builder: map(world.builder),
    producer: map(world.producer),
    resource: map(world.resource),
    building: map(world.building),
    rally: map(world.rally),
    los: map(world.los),
    healer: map(world.healer),
    fauna: map(world.fauna),
    garrison: map(world.garrison),
    carcass: [...world.carcass],
  };
}

/** Capture the full sim state as an INDEPENDENT object (safe to keep around).
 *  Cosmetic-only data (events, render meshes) is excluded — the sim never reads
 *  it. Take this at a step boundary (dead entities already flushed). */
export function snapshot(world: World): WorldSnapshot {
  return clone(build(world));
}

/** Capture the state directly as a JSON string. Cheaper than `snapshot` for
 *  autosave (a single stringify, no clone round-trip) — and exactly what the
 *  gzip layer needs. Reads the live world synchronously, so call + use the
 *  result before stepping again. */
export function snapshotJSON(world: World): string {
  return JSON.stringify(build(world));
}

const COMPONENT_STORES = [
  "transform", "owner", "ident", "health", "move", "combat", "gatherer",
  "builder", "producer", "resource", "building", "rally", "los", "healer",
  "fauna", "garrison",
] as const;

function invalidSave(message: string): never {
  throw new Error(`Invalid save: ${message}`);
}

/** Validate all top-level shape constraints before allocating a World. */
function validateSnapshot(snap: unknown): asserts snap is WorldSnapshot {
  if (!snap || typeof snap !== "object" || Array.isArray(snap)) invalidSave("snapshot must be an object");
  const s = snap as Record<string, unknown>;
  if (s.version !== SNAPSHOT_VERSION) {
    throw new Error(`Incompatible save (v${String(s.version)}, expected v${SNAPSHOT_VERSION})`);
  }
  for (const field of ["cols", "rows", "size", "seed", "tick", "nextEid", "rng"] as const) {
    if (typeof s[field] !== "number" || !Number.isFinite(s[field])) invalidSave(`${field} must be a finite number`);
  }
  const cols = s.cols, rows = s.rows, size = s.size;
  if (typeof cols !== "number" || typeof rows !== "number" || typeof size !== "number" || !Number.isInteger(cols) || cols <= 0 || !Number.isInteger(rows) || rows <= 0 || size <= 0) {
    invalidSave("dimensions must be positive");
  }
  if (!Array.isArray(s.blocked) || s.blocked.length !== cols * rows) {
    invalidSave("blocked length must match map dimensions");
  }
  if (s.terrain !== undefined) validateTerrainRecipe(s.terrain, cols, rows);
  const blocked = s.blocked as unknown[];
  if (blocked.some((v) => !Number.isInteger(v) || (v as number) < 0 || (v as number) > 255)) invalidSave("blocked must contain byte values");
  for (const field of ["resourceHexKeys", "dropsites", "players", "carcass"] as const) {
    if (!Array.isArray(s[field])) invalidSave(`${field} must be an array`);
  }
  for (const field of COMPONENT_STORES) {
    const entries = s[field];
    if (!Array.isArray(entries)) invalidSave(`${field} must be an array`);
    const eids = new Set<number>();
    for (const entry of entries) {
      if (!Array.isArray(entry) || entry.length !== 2 || !Number.isInteger(entry[0]) || entry[0] <= 0 || !entry[1] || typeof entry[1] !== "object") {
        invalidSave(`${field} contains an invalid component entry`);
      }
      const eid = entry[0] as number;
      if (eids.has(eid)) invalidSave(`${field} contains duplicate entity ${eid}`);
      eids.add(eid);
    }
  }
  const identEids = new Set((s.ident as unknown[]).map((entry) => (entry as unknown[])[0] as number));
  for (const field of COMPONENT_STORES) {
    for (const entry of s[field] as unknown[]) {
      if (!identEids.has((entry as unknown[])[0] as number)) invalidSave(`${field} references an entity without ident`);
    }
  }
  if ((s.resourceHexKeys as unknown[]).some((key) => !Number.isInteger(key) || (key as number) < 0 || (key as number) >= (s.cols as number) * (s.rows as number))) {
    invalidSave("resourceHexKeys contains an out-of-bounds hex");
  }
  if ((s.carcass as unknown[]).some((eid) => !Number.isInteger(eid) || !identEids.has(eid as number))) {
    invalidSave("carcass references an unknown entity");
  }
  const playerIds = new Set<number>();
  for (const player of s.players as unknown[]) {
    if (!player || typeof player !== "object" || !Number.isInteger((player as { id?: unknown }).id)) invalidSave("players contains an invalid player");
    const id = (player as { id: number }).id;
    if (playerIds.has(id)) invalidSave(`players contains duplicate player ${id}`);
    playerIds.add(id);
  }
  for (const entry of s.owner as unknown[]) {
    const owner = (entry as [number, { player?: unknown }])[1];
    if (!Number.isInteger(owner.player) || !playerIds.has(owner.player as number)) invalidSave("owner references an unknown player");
  }
  for (const drop of s.dropsites as unknown[]) {
    if (!Array.isArray(drop) || drop.length !== 2 || !Number.isInteger(drop[0]) || !playerIds.has(drop[0] as number) || !Array.isArray(drop[1]) || drop[1].some((eid) => !Number.isInteger(eid) || !identEids.has(eid as number))) {
      invalidSave("dropsites contains an invalid reference");
    }
  }
}

function validateTerrainRecipe(value: unknown, cols: number, rows: number): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalidSave("terrain must be an object");
  const terrain = value as Record<string, unknown>;
  if (terrain.base !== undefined && (
    typeof terrain.base !== "string" || !TERRAIN_TYPES.includes(terrain.base as TerrainType)
  )) invalidSave("terrain base must be a known terrain type");
  if (terrain.features !== undefined) {
    if (!Array.isArray(terrain.features)) invalidSave("terrain features must be an array");
    for (const value of terrain.features) {
      if (!value || typeof value !== "object" || Array.isArray(value)) invalidSave("terrain contains an invalid feature");
      const feature = value as Record<string, unknown>;
      if (typeof feature.type !== "string" || !TERRAIN_TYPES.includes(feature.type as TerrainType)) invalidSave("terrain feature type must be known");
      if (!Number.isInteger(feature.count) || (feature.count as number) < 0) invalidSave("terrain feature count must be a non-negative integer");
      if (!Number.isInteger(feature.minSize) || (feature.minSize as number) < 1) invalidSave("terrain feature minSize must be a positive integer");
      if (!Number.isInteger(feature.maxSize) || (feature.maxSize as number) < (feature.minSize as number)) invalidSave("terrain feature maxSize must be at least minSize");
    }
  }
  if (terrain.protectedHexes !== undefined) {
    if (!Array.isArray(terrain.protectedHexes)) invalidSave("terrain protectedHexes must be an array");
    for (const value of terrain.protectedHexes) {
      if (!value || typeof value !== "object" || Array.isArray(value)) invalidSave("terrain contains an invalid protected hex");
      const hex = value as Record<string, unknown>;
      if (!Number.isInteger(hex.col) || !Number.isInteger(hex.row) || (hex.col as number) < 0 || (hex.row as number) < 0 || (hex.col as number) >= cols || (hex.row as number) >= rows) {
        invalidSave("terrain protectedHexes contains an out-of-bounds hex");
      }
    }
  }
}

/** Rebuild a live World from a snapshot. Deterministic: terrain from the seed,
 *  components verbatim, and occupancy (blocked tiles / resource tiles / drop
 *  sites) re-derived from the restored entities exactly as spawn.ts builds it. */
export function restore(snap: WorldSnapshot, _reg: Registry): World {
  validateSnapshot(snap);
  snap = clone(snap); // own the data — the restored world must not alias the caller's snapshot
  const terrain = snap.terrain ?? {};
  const hexes = matchHexes(snap.cols, snap.rows, snap.size, snap.seed, terrain);
  const world = new World(snap.cols, snap.rows, snap.size, hexes, snap.seed);
  setTerrainRecipe(world, terrain);

  world.tick = snap.tick;
  world.setEidCounter(snap.nextEid);
  world.rng.setState(snap.rng);

  // occupancy verbatim (exact match with the saved world — see snapshot comment)
  world.blocked.set(snap.blocked);
  for (const k of snap.resourceHexKeys) world.resourceHexKeys.add(k);
  for (const [p, eids] of snap.dropsites) world.dropsitesByPlayer.set(p, new Set(eids));

  for (const p of snap.players) world.players.set(p.id, p);

  const load = <T>(m: Map<Eid, T>, entries: Entry<T>[]) => {
    for (const [eid, v] of entries) m.set(eid, v);
  };
  load(world.transform, snap.transform);
  load(world.owner, snap.owner);
  load(world.ident, snap.ident);
  load(world.health, snap.health);
  load(world.move, snap.move);
  load(world.combat, snap.combat);
  load(world.gatherer, snap.gatherer);
  load(world.builder, snap.builder);
  load(world.producer, snap.producer);
  load(world.resource, snap.resource);
  load(world.building, snap.building);
  load(world.rally, snap.rally);
  load(world.los, snap.los);
  load(world.healer, snap.healer);
  load(world.fauna, snap.fauna);
  load(world.garrison, snap.garrison);
  for (const e of snap.carcass) world.carcass.add(e);

  // rebuild per-hex routing penalties from the restored resource nodes (derived,
  // not stored — same values spawn.ts assigns)
  for (const [eid, idn] of world.ident) {
    if (idn.kind !== "resource") continue;
    const tr = world.transform.get(eid);
    if (!tr) continue;
    const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
    // fish schools live on water and register NO prop footprint (see spawnFish) —
    // skip them so a restored world matches a freshly-spawned one exactly.
    if (h && !world.isWater(h.col, h.row)) {
      world.propCost.set(world.hkey(h.col, h.row), propTileCost(idn.defId));
      world.propPos.set(world.hkey(h.col, h.row), { x: tr.pos.x, z: tr.pos.y, r: propRadius(idn.defId, world.size) });
    }
  }
  // re-derive LAND building sub-tile footprints (buildingHexKeys + propCost/propPos),
  // exactly as spawnBuilding registers them — not stored, so a restored world's
  // pathing/collision matches a freshly-built one (the determinism test's safety net).
  for (const [eid, b] of world.building) {
    const idn = world.ident.get(eid);
    if (!idn) continue;
    const k = world.hkey(b.origin.x, b.origin.y);
    const water = world.isWater(b.origin.x, b.origin.y);
    // Re-derive the building's CONTACT radius from current data (not the value baked
    // at its original spawn) so a CONTINUE picks up tuning changes — e.g. a wall built
    // before its contact radius grew still becomes attackable from the adjacent tile.
    const t = world.transform.get(eid);
    if (t) t.radius = Math.max(buildingRadius(idn.defId, world.size), world.size * 0.35);
    // A LAND building's tile is PASSABLE-occupied ground at all times (foundation
    // included), with a sub-tile repel circle only for footprint buildings. Mirrors
    // spawnBuilding so a restored world pathways/collides identically to a live one.
    if (!water) {
      world.buildingHexKeys.add(k);
      const r = b.solid || b.gate ? 0 : buildingRadius(idn.defId, world.size);
      if (r > 0) {
        world.propCost.set(k, buildingTileCost(idn.defId));
        const c = world.hexCenter(b.origin.x, b.origin.y);
        world.propPos.set(k, { x: c.x, z: c.y, r });
      }
    }
    // Rebuild completed-structure indexes used by dock occupancy, rampart geometry,
    // renderer connectivity, and team-aware gate portals. Foundations stay open.
    if (!b.isFoundation) {
      if (water) world.solidHexKeys.add(k);
      else if (b.gate) {
        const owner = world.owner.get(eid)?.player;
        if (owner !== undefined) world.gateHexKeys.set(k, owner);
      } else if (b.solid) world.solidHexKeys.add(k);
    }
  }

  // re-attach the per-world gather/production helpers — without this a restored
  // game's workers (and bots) can't find a drop-site and never resume gathering
  // until the player happens to issue a command.
  attachWorldHelpers(world);
  return world;
}
